"use client";

import { usePathname } from "next/navigation";
import { ImageryUnifiedShellHeader } from "@/components/imagery/ImageryUnifiedShellHeader";
import {
	ImagerySupportFab,
	shouldShowImagerySupportFab,
} from "@/components/imagery/ImagerySupportFab";
import { ImageryCookieHeaderSlotSync } from "@/components/imagery/ImageryCookieHeaderSlotSync";
import { IMAGERY_HEADER_INSET } from "@/components/imagery/imageryLayoutInset";
import { useImageryRequestsIndexChrome } from "@/contexts/ImageryRequestsIndexChromeContext";

/** Pads main content below the fixed imagery header (matches inset + rounded chrome height). */
function ImageryMainBelowFixedHeader({
	children,
	className = "",
	/** Marketing /imagery (logged-in): lock main height to viewport minus header so body does not scroll */
	fitViewport = false,
	/** When false, no top padding and full viewport height (header hidden). */
	hasFixedHeader = true,
}: {
	children: React.ReactNode;
	/** e.g. flex-1 min-h-0 when inside a flex column shell */
	className?: string;
	fitViewport?: boolean;
	hasFixedHeader?: boolean;
}) {
	const mainHeight = hasFixedHeader
		? `calc(100dvh - ${IMAGERY_HEADER_INSET})`
		: "100dvh";

	return (
		<div
			className={`relative w-full min-h-0 bg-gray-50 ${className}`}
			style={{
				paddingTop: hasFixedHeader ? IMAGERY_HEADER_INSET : 0,
			}}
		>
			<div
				className={`relative z-[1] min-h-0 ${
					fitViewport ? "overflow-y-auto lg:overflow-hidden" : ""
				}`}
				style={
					fitViewport
						? {
								height: mainHeight,
								maxHeight: mainHeight,
							}
						: undefined
				}
			>
				{children}
			</div>
		</div>
	);
}

export function ImageryAppShell({ children }: { children: React.ReactNode }) {
	const pathname = usePathname() ?? "";
	const normalized =
		pathname.length > 1 && pathname.endsWith("/")
			? pathname.slice(0, -1)
			: pathname;
	const isImageryMarketingLanding = normalized === "/imagery";
	const { hideShellHeaderOnRequestsIndex } = useImageryRequestsIndexChrome();
	/** Marketing: never chrome. `/imagery/requests` index only: hide during check-in / loading (see requests page). Sub-routes always show header. */
	const hideImageryShellHeader =
		normalized === "/imagery" ||
		(normalized === "/imagery/requests" && hideShellHeaderOnRequestsIndex);
	const mountImageryCookieSlotSync = normalized !== "/imagery";
	const showSupportFab = shouldShowImagerySupportFab(normalized);

	return (
		<div className="flex min-h-screen w-full flex-col bg-gray-50">
			{mountImageryCookieSlotSync ? (
				<ImageryCookieHeaderSlotSync />
			) : null}
			{hideImageryShellHeader ? null : <ImageryUnifiedShellHeader />}
			<ImageryMainBelowFixedHeader
				className="min-h-0 flex-1"
				fitViewport={isImageryMarketingLanding}
				hasFixedHeader={!hideImageryShellHeader}
			>
				{children}
			</ImageryMainBelowFixedHeader>
			{showSupportFab ? <ImagerySupportFab /> : null}
		</div>
	);
}
